import { FC, useEffect, useMemo } from "react";

import Box from "@mui/material/Box";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import { QuickBooksMappingEntityType } from "@simplyk/common";
import { useForm } from "react-hook-form";

import { trpc } from "@/common/helpers/trpc";
import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardSelect } from "@/dashboard-design-system/Select";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";
import { useLiveFormEditorContext } from "@/features/LiveFormEditor/common/LiveFormEditorContext";
import { FormStepContainer } from "@/features/LiveFormEditor/Steps/FormStepContainer";
import { useFormSaveAndNavigate } from "@/features/LiveFormEditor/Steps/useFormSaveAndNavigate";

export interface StepQuickBooksMappingFormData {
  qbAccountId: string;
  qbClassId: string;
  qbProductServiceId: string;
}

export const StepQuickBooksMapping: FC = () => {
  const { t } = useTranslate();
  const { apiFormObject: apiDonationForm } = useLiveFormEditorContext<"donation-form">();

  const { data: qbMappingsData } = trpc.getQuickBooksMappings.useQuery();

  const incomeAccounts = qbMappingsData?.incomeAccounts ?? [];
  const qbClasses = qbMappingsData?.classes ?? [];
  const qbProductsAndServices = qbMappingsData?.productsAndServices ?? [];
  const mappings = qbMappingsData?.mappings ?? [];

  const existingMapping = mappings.find(
    (m) => m.entityType === QuickBooksMappingEntityType.Campaign && m.entityId === apiDonationForm.id
  );

  const formValues = useMemo<StepQuickBooksMappingFormData>(
    () => ({
      qbAccountId: existingMapping?.qbAccountId ?? "",
      qbClassId: existingMapping?.qbClassId ?? "",
      qbProductServiceId: existingMapping?.qbProductServiceId ?? "",
    }),
    [existingMapping]
  );

  const form = useForm<StepQuickBooksMappingFormData>({ defaultValues: formValues });
  const qbAccountId = form.watch("qbAccountId");

  useEffect(() => {
    form.reset(formValues);
  }, [formValues]); // eslint-disable-line react-hooks/exhaustive-deps
  const { mutateAsync: saveMapping } = trpc.saveQuickBooksMapping.useMutation();

  const { handleSaveAndNext, handleSaveAndBack } = useFormSaveAndNavigate<StepQuickBooksMappingFormData>({
    type: "donation-form",
    form,
    alwaysSaveOnNext: true,
    saveData: async (data) => {
      if (data.qbAccountId) {
        await saveMapping({
          entityType: QuickBooksMappingEntityType.Campaign,
          entityId: apiDonationForm.id,
          qbAccountId: data.qbAccountId,
          qbClassId: data.qbClassId || null,
          qbProductServiceId: data.qbProductServiceId || null,
        });
      }
      return { data: apiDonationForm, error: null };
    },
  });

  return (
    <FormStepContainer
      form={form}
      actionsProps={{
        onNext: handleSaveAndNext,
        onBack: handleSaveAndBack,
        nextButtonLabel: qbAccountId ? "next" : "skip",
      }}
      title={t("dashboard", "quickbooks.lfe.title")}
      description={t("dashboard", "quickbooks.lfe.subtitle")}
    >
      <Box
        sx={(theme) => ({
          backgroundColor: theme.palette.surface.neutral.supershy,
          borderRadius: 1,
          paddingX: 3,
          paddingY: 2,
        })}
      >
        <Stack spacing={1.5}>
          <DashboardTypography variant="subtitle1" vibe="text-neutral-moderate">
            {t("dashboard", "quickbooks.lfe.fieldsTitle")}
          </DashboardTypography>
          <Stack spacing={3}>
            <DashboardSelect
              control={form.control}
              name="qbAccountId"
              label={`${t("dashboard", "quickbooks.lfe.qbAccount")}*`}
              placeholder={t("dashboard", "quickbooks.lfe.qbAccountPlaceholder")}
              size="medium"
            >
              {incomeAccounts.map((account) => (
                <MenuItem key={account.id} value={account.id}>
                  {account.name}
                </MenuItem>
              ))}
            </DashboardSelect>
            {qbClasses.length > 0 && (
              <DashboardSelect
                control={form.control}
                name="qbClassId"
                label={t("dashboard", "quickbooks.lfe.qbClass")}
                placeholder={t("dashboard", "quickbooks.connected.table.selectClass")}
                size="medium"
              >
                {qbClasses.map((cls) => (
                  <MenuItem key={cls.id} value={cls.id}>
                    {cls.name}
                  </MenuItem>
                ))}
              </DashboardSelect>
            )}
            {qbProductsAndServices.length > 0 && (
              <DashboardSelect
                control={form.control}
                name="qbProductServiceId"
                label={t("dashboard", "quickbooks.lfe.qbProductService")}
                placeholder={t("dashboard", "quickbooks.connected.table.selectProductService")}
                size="medium"
              >
                {qbProductsAndServices.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    {item.name}
                  </MenuItem>
                ))}
              </DashboardSelect>
            )}
          </Stack>
        </Stack>
      </Box>
    </FormStepContainer>
  );
};
